/**
 * Zoom Controls Component
 *
 * Week 2: Dual Scale System
 * Provides manual zoom controls (25%-400%) with keyboard shortcuts
 */

import React from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useResponsiveStore } from '@/stores/editor/responsive-store';
import { cn } from '@/lib/utils';

export interface ZoomControlsProps {
  className?: string;
  showPercentage?: boolean;
  showScaleBreakdown?: boolean; // Week 2 Day 6.1: Show detailed scale breakdown
  size?: 'sm' | 'default' | 'lg';
}

export const ZoomControls: React.FC<ZoomControlsProps> = ({
  className,
  showPercentage = true,
  showScaleBreakdown = false,
  size = 'default',
}) => {
  const activeViewport = useResponsiveStore((state) => state.activeViewport);
  const userZoom = useResponsiveStore((state) => state.userZoom);
  const getViewportScale = useResponsiveStore((state) => state.getViewportScale);
  const getEffectiveScale = useResponsiveStore((state) => state.getEffectiveScale);
  const zoomInStore = useResponsiveStore((state) => state.zoomIn);
  const zoomOutStore = useResponsiveStore((state) => state.zoomOut);
  const resetZoomStore = useResponsiveStore((state) => state.resetZoom);

  // Week 2 Day 6.1: Calculate scale breakdown
  const viewportScale = getViewportScale(activeViewport);
  const effectiveScale = getEffectiveScale(activeViewport);

  const isZoomedIn = userZoom >= 4.0; // Max zoom 400%
  const isZoomedOut = userZoom <= 0.25; // Min zoom 25%
  const isDefaultZoom = Math.abs(userZoom - 1.0) < 0.01; // Close to 100%

  const zoomPercentage = Math.round(userZoom * 100);

  // Zoom action handlers
  const zoomIn = () => zoomInStore();
  const zoomOut = () => zoomOutStore();
  const resetZoom = () => resetZoomStore();

  // Button and icon sizes based on size prop
  // When size='sm', use h-7 buttons to match smaller ViewportSelector (h-8)
  const buttonSize = size === 'sm' ? 'h-7 w-7' : size === 'lg' ? 'h-12 w-12' : 'h-11 w-11';
  const iconSize = size === 'sm' ? 13 : size === 'lg' ? 22 : 18;

  // For 'sm' size, use minimal styling to match ViewportSelector height
  const isCompact = size === 'sm';

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex items-center rounded-md bg-background border border-border',
          isCompact ? 'h-8 gap-0.5 px-1' : 'gap-1 p-1',
          className
        )}
      >
        {/* Zoom Out Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={zoomOut}
              disabled={isZoomedOut}
              className={buttonSize}
              style={{
                transition: 'background-color 150ms ease-in-out',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              aria-label="Zoom out"
              data-testid="zoom-out"
            >
              <ZoomOut size={iconSize} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Zoom Out (Cmd/Ctrl -)</p>
            <p className="text-xs text-muted-foreground">Min: 25%</p>
          </TooltipContent>
        </Tooltip>

        {/* Zoom Percentage Display */}
        {showPercentage && (
          <div
            className={cn(
              "flex items-center justify-center font-mono font-medium select-none",
              isCompact ? "text-[11px] min-w-[2.5rem] px-1" : "text-xs min-w-[3.5rem] px-2"
            )}
            style={{
              color: isDefaultZoom ? 'hsl(var(--primary))' : 'inherit',
            }}
            aria-live="polite"
            aria-label={`Current zoom: ${zoomPercentage}%`}
            data-testid="zoom-display"
          >
            {zoomPercentage}%
          </div>
        )}

        {/* Zoom In Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={zoomIn}
              disabled={isZoomedIn}
              className={buttonSize}
              style={{
                transition: 'background-color 150ms ease-in-out',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              aria-label="Zoom in"
              data-testid="zoom-in"
            >
              <ZoomIn size={iconSize} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Zoom In (Cmd/Ctrl +)</p>
            <p className="text-xs text-muted-foreground">Max: 400%</p>
          </TooltipContent>
        </Tooltip>

        {/* Reset Zoom Button */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={resetZoom}
              disabled={isDefaultZoom}
              className={buttonSize}
              style={{
                transition: 'background-color 150ms ease-in-out',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              aria-label="Reset zoom to 100%"
            >
              <RotateCcw size={iconSize} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Reset Zoom (Cmd/Ctrl 0)</p>
            <p className="text-xs text-muted-foreground">Back to 100%</p>
          </TooltipContent>
        </Tooltip>

        {/* Week 2 Day 6.1: Scale Breakdown Display */}
        {showScaleBreakdown && (
          <div
            className="ml-2 flex items-center gap-1 pl-2"
            style={{ borderLeft: '1px solid hsl(var(--border))' }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="cursor-help rounded px-2 py-1 font-mono text-xs"
                  style={{ backgroundColor: 'hsl(var(--accent))' }}
                  aria-label={`Scale breakdown: Viewport ${Math.round(viewportScale * 100)}%, User ${Math.round(userZoom * 100)}%, Effective ${Math.round(effectiveScale * 100)}%`}
                >
                  <div className="flex items-center gap-1">
                    <span className="text-muted-foreground">V:</span>
                    <span className="font-medium">{Math.round(viewportScale * 100)}%</span>
                    <span className="text-muted-foreground">×</span>
                    <span className="text-muted-foreground">U:</span>
                    <span className="font-medium">{Math.round(userZoom * 100)}%</span>
                    <span className="text-muted-foreground">=</span>
                    <span className="font-medium" style={{ color: 'hsl(var(--primary))' }}>{Math.round(effectiveScale * 100)}%</span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs">
                <div className="space-y-1">
                  <p className="font-semibold">Scale Breakdown</p>
                  <div className="space-y-0.5 text-xs">
                    <div className="flex justify-between gap-4">
                      <span className="text-muted-foreground">Viewport Scale:</span>
                      <span className="font-mono font-medium">{Math.round(viewportScale * 100)}%</span>
                    </div>
                    <div className="flex justify-between gap-4">
                      <span className="text-muted-foreground">User Zoom:</span>
                      <span className="font-mono font-medium">{Math.round(userZoom * 100)}%</span>
                    </div>
                    <div className="mt-1 flex justify-between gap-4 border-t pt-1">
                      <span className="font-medium">Effective Scale:</span>
                      <span className="font-mono font-semibold" style={{ color: 'hsl(var(--primary))' }}>{Math.round(effectiveScale * 100)}%</span>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Viewport scale adapts canvas to container.
                    <br />
                    User zoom is your manual zoom control.
                    <br />
                    Effective scale = Viewport × User.
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

/**
 * Compact Zoom Controls (minimal variant)
 * Only shows zoom percentage and +/- buttons, no reset
 */
export const CompactZoomControls: React.FC<ZoomControlsProps> = ({
  className,
  size = 'sm',
}) => {
  const userZoom = useResponsiveStore((state) => state.userZoom);
  const zoomIn = useResponsiveStore((state) => state.zoomIn);
  const zoomOut = useResponsiveStore((state) => state.zoomOut);

  const isZoomedIn = userZoom >= 4.0;
  const isZoomedOut = userZoom <= 0.25;
  const zoomPercentage = Math.round(userZoom * 100);

  // Minimum 44px touch targets for WCAG accessibility
  const buttonSize = size === 'sm' ? 'h-9 w-9' : 'h-11 w-11';
  const iconSize = size === 'sm' ? 14 : 16;

  return (
    <div
      className={cn(
        'flex items-center gap-0.5 rounded p-0.5',
        className
      )}
      style={{
        backgroundColor: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
      }}
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={zoomOut}
        disabled={isZoomedOut}
        className={buttonSize}
        style={{ transition: 'background-color 150ms ease-in-out' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        aria-label="Zoom out"
      >
        <ZoomOut size={iconSize} />
      </Button>

      <div
        className="min-w-[2.5rem] select-none px-1 text-center font-mono text-xs font-medium"
        aria-live="polite"
      >
        {zoomPercentage}%
      </div>

      <Button
        variant="ghost"
        size="icon"
        onClick={zoomIn}
        disabled={isZoomedIn}
        className={buttonSize}
        style={{ transition: 'background-color 150ms ease-in-out' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        aria-label="Zoom in"
      >
        <ZoomIn size={iconSize} />
      </Button>
    </div>
  );
};

/**
 * Zoom Dropdown (advanced variant)
 * Includes preset zoom levels dropdown
 */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getRecommendedZoomLevels } from '@/utils/viewport-scale';

export const ZoomDropdown: React.FC<ZoomControlsProps> = ({
  className,
  size = 'default',
}) => {
  const userZoom = useResponsiveStore((state) => state.userZoom);
  const setUserZoom = useResponsiveStore((state) => state.setUserZoom);
  const zoomIn = useResponsiveStore((state) => state.zoomIn);
  const zoomOut = useResponsiveStore((state) => state.zoomOut);
  const resetZoom = useResponsiveStore((state) => state.resetZoom);

  const isZoomedIn = userZoom >= 4.0;
  const isZoomedOut = userZoom <= 0.25;
  const isDefaultZoom = Math.abs(userZoom - 1.0) < 0.01;

  const zoomPercentage = Math.round(userZoom * 100);
  const recommendedLevels = getRecommendedZoomLevels();

  // Minimum 44px touch targets for WCAG accessibility
  const buttonSize = size === 'sm' ? 'h-9 w-9' : size === 'lg' ? 'h-12 w-12' : 'h-11 w-11';
  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 22 : 18;

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md p-1',
        className
      )}
      style={{
        backgroundColor: 'hsl(var(--muted))',
        border: '1px solid hsl(var(--border))',
      }}
    >
      {/* Zoom Out Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={zoomOut}
        disabled={isZoomedOut}
        className={buttonSize}
        style={{ transition: 'background-color 150ms ease-in-out' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        aria-label="Zoom out"
      >
        <ZoomOut size={iconSize} />
      </Button>

      {/* Zoom Percentage Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-11 min-w-[4rem] font-mono text-xs font-medium"
            style={{
              color: isDefaultZoom ? 'hsl(var(--primary))' : 'inherit',
              transition: 'background-color 150ms ease-in-out',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            {zoomPercentage}%
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="min-w-[120px]">
          {recommendedLevels.map((level) => (
            <DropdownMenuItem
              key={level.value}
              onClick={() => setUserZoom(level.value)}
              className="justify-between font-mono"
              style={{
                backgroundColor: Math.abs(userZoom - level.value) < 0.01 ? 'hsl(var(--accent))' : 'transparent',
                fontWeight: Math.abs(userZoom - level.value) < 0.01 ? 600 : 400,
              }}
            >
              {level.label}
              {Math.abs(userZoom - level.value) < 0.01 && (
                <span className="ml-2" style={{ color: 'hsl(var(--primary))' }}>✓</span>
              )}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={resetZoom}
            disabled={isDefaultZoom}
            className="font-medium"
          >
            Reset to 100%
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Zoom In Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={zoomIn}
        disabled={isZoomedIn}
        className={buttonSize}
        style={{ transition: 'background-color 150ms ease-in-out' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        aria-label="Zoom in"
      >
        <ZoomIn size={iconSize} />
      </Button>

      {/* Reset Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={resetZoom}
        disabled={isDefaultZoom}
        className={buttonSize}
        style={{ transition: 'background-color 150ms ease-in-out' }}
        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'hsl(var(--accent))'}
        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        aria-label="Reset zoom to 100%"
      >
        <RotateCcw size={iconSize} />
      </Button>
    </div>
  );
};
